import { Pagination, ReplenishmentPlan } from '@ekkogmbh/apisdk';
import { Add, Delete, Edit } from '@mui/icons-material';
import { useCallback, useState } from 'react';
import { ColumnDef, DataTable, dataTableHighlight } from '../../Common/Components/DataTable';
import { GenericDialog } from '../../Common/Components/GenericDialog';
import Permissions from 'src/Common/Permissions';
import { ReplenishmentPlanPanel } from './ReplenishmentPlanPanel';
import { ContentControl } from 'src/Common/Components/ContentControl';
import { ContentControlButton } from 'src/Common/Components/ContentControl/ContentControlButton';
import ReplenishmentPlanStatesTableBody from './ReplenishmentPlanStatesTableBody';
import useEkko from 'src/Common/Providers/EkkoProvider';
import { useContentControl } from 'src/Common/Stores/ContentControlProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import useNavigation from 'src/Common/Providers/NavigationProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';
import ScrollableStack from 'src/Common/Components/ScrollableStack';

// Chips are ~32px tall; 48px gives comfortable vertical padding so scroll
// indicators on the States column align with text in the other columns.
const ROW_MIN_HEIGHT = 48;

export const ReplenishmentPlanManagementContent = (): React.JSX.Element => {
  const { api } = useEkko();
  const { setPanelOpen } = useContentControl();
  const { scrollTop } = useNavigation();
  const { refresh } = usePagination();

  const [, startLoading] = useRequest();
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [selectedPlan, setSelectedPlan] = useState<ReplenishmentPlan>();

  const dismiss = useCallback(() => {
    setDialogOpen(false);
    setPanelOpen(false);
    setSelectedPlan(undefined);
  }, []);

  const deletePlan = useCallback(() => {
    if (selectedPlan === undefined) {
      return;
    }

    startLoading(
      () => api.deleteReplenishmentPlan(selectedPlan),
      () => {
        dismiss();
        refresh();
      },
    );
  }, [selectedPlan]);

  const fetchPlans = useCallback(async (pagination: Pagination) => await api.getReplenishmentPlans(pagination), [api]);

  const columns: ColumnDef[] = [
    {
      header: 'Name',
      body: (entry: ReplenishmentPlan, searchText?: string) => (
        <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
          {searchText ? dataTableHighlight(entry.name ?? '', [searchText]) : entry.name}
        </ScrollableStack>
      ),
      filterField: 'name',
      sortField: 'RP.name',
      proportionalContent: '40%',
    },
    {
      header: 'Coordinate',
      body: (entry: ReplenishmentPlan, searchText?: string) => (
        <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
          {searchText ? dataTableHighlight(entry.coordinate ?? '', [searchText]) : entry.coordinate}
        </ScrollableStack>
      ),
      filterField: 'coordinate',
      sortField: 'N.materializedPath',
      proportionalContent: '60%',
    },
    {
      header: 'States',
      body: (entry: ReplenishmentPlan) => (
        <ReplenishmentPlanStatesTableBody states={entry.linearStateMachineDefinition.states} />
      ),
      fitContent: true,
    },
  ];

  return (
    <ContentControl
      panel={<ReplenishmentPlanPanel replenishmentPlan={selectedPlan} closeHandler={dismiss} />}
      dialog={
        <GenericDialog
          type="confirmation"
          maxWidth={'sm'}
          fullWidth={true}
          centered={true}
          open={isDialogOpen}
          title={'Delete Replenishment Plan'}
          text={selectedPlan?.name ?? ''}
          onClose={() => {
            dismiss();
            scrollTop();
          }}
          onConfirm={deletePlan}
        />
      }
      buttons={[
        <ContentControlButton
          key={'add'}
          content={<Add />}
          tooltip={'Add Replenishment Plan'}
          onClick={() => {
            dismiss();
            setPanelOpen(true);
            scrollTop();
          }}
          requiredPermission={Permissions.EKANBAN_WRITE}
        />,
      ]}
    >
      <DataTable
        actions={[
          {
            title: 'Edit',
            icon: <Edit />,
            onClick: (entry: ReplenishmentPlan) => {
              dismiss();
              setSelectedPlan(entry);
              setPanelOpen(true);
              scrollTop();
            },
          },
          {
            title: 'Delete',
            icon: <Delete />,
            onClick: (entry: ReplenishmentPlan) => {
              dismiss();
              setSelectedPlan(entry);
              setDialogOpen(true);
            },
          },
        ]}
        columns={columns}
        fetchItems={fetchPlans}
      />
    </ContentControl>
  );
};
